<script>
import { setShareRubricByDomain } from "@/api";
import debounce from "../../utils/debounce";

export default {
  name: "RubricShareModal",
  props: {
    rubric: {
      type: Object,
      default: () => {},
    },
    zIndex: {
      type: Number,
      default: 1000,
    },
    showBackdrop: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["invalidateRubric"],
  data() {
    return {
      checkBoxValue: this.rubric?.shareLink.isSharedWithDomain ?? false,
    };
  },
  computed: {
    url() {
      if (!this.rubric) {
        return "";
      }
      const href = this.$router.resolve({
        name: "shared-rubric",
        params: { slug: this.rubric.shareLink.slug },
      }).href;

      return `${window.location.protocol}//${window.location.host}${href}`;
    },
    isShared() {
      return this.rubric?.shareLink.isSharedWithDomain ?? false;
    },
    canShare() {
      return this.$store.state.user?.domain?.shareable || false;
    },

    userDomain() {
      return this.$store.state.user?.domain?.url ?? "";
    },
  },
  watch: {
    rubric: function (val) {
      if (val) {
        this.checkBoxValue = this.isShared;
      }
    },
  },
  methods: {
    onCopy() {
      navigator.clipboard.writeText(this.url);
      this.$showToastSuccess(this.$t("rubrics.rubricLinkCopied"));
    },

    setShare: debounce(function (value) {
      setShareRubricByDomain(this.rubric.shareLink.id, value).then(() => {
        this.$emit("invalidateRubric", this.rubric);
        this.$showToastSuccess(this.$t("rubrics.sharingPermissionsSaved"));
      });
    }, 500),
  },
};
</script>

<template>
  <eg-modal
    id="rubric-share-modal"
    size="md"
    centered
    :title="$t('rubrics.shareRubric')"
    no-stacking
    :z-index="zIndex"
    :show-backdrop="showBackdrop"
  >
    <template #modal-title>
      <div>
        <eg-b-icon icon="link45deg" />
        {{ $t("rubrics.shareRubricBtn") }}
      </div>
    </template>
    <div class="share-container">
      <label class="paragraph-s">{{ $t("rubrics.rubricLink") }}</label>
      <eg-b-input-group class="input-wrapper">
        <eg-input class="shared-url" readonly size="medium" :value="url" />
        <eg-b-input-group-append>
          <eg-button type="primary" size="small" icon="copy" @click="onCopy">
            {{ $t("rubrics.copy") }}
          </eg-button>
        </eg-b-input-group-append>
      </eg-b-input-group>
      <div v-if="canShare" class="d-flex align-items-center mt-3">
        <eg-b-form-checkbox
          v-model="checkBoxValue"
          switch
          size="md"
          @change="setShare"
        />
        <div class="ml-2 d-flex flex-column">
          {{ $t("rubrics.anyoneWithDomain", { domain: userDomain }) }}
          <span class="text-secondary">
            <small>
              {{ $t("rubrics.domainAccessInfo") }}
            </small>
          </span>
        </div>
      </div>
    </div>
    <template #modal-footer="{ close }">
      <eg-button type="secondary" @click="close">
        {{ $t("common.close") }}</eg-button
      >
    </template>
  </eg-modal>
</template>

<style scoped lang="scss">
.share-container {
  padding-bottom: 2.5rem;
}
input.form-control[readonly] {
  background-color: white;
}
.shared-url {
  flex-grow: 1;
}
.input-wrapper {
  border-radius: var(--radius-rd-300, 8px);
  border: 1px solid var(--color-stroke-neutral-default);
  ::v-deep {
    .eg-input__control {
      border: none;
    }
    .eg-new-button {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}
</style>
